import React, { useState } from 'react';
import styled from 'styled-components';
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';

import FirebaseImage from './FirebaseImage';
import {
  PORTFOLIO_SOURCE_LABELS,
} from '../../Utils/portfolio';

const BeforeAfterComparison = ({ comparison, className }) => {
  const [position, setPosition] = useState(50);
  if (!comparison?.before?.url || !comparison?.after?.url) return null;

  const beforeSource = PORTFOLIO_SOURCE_LABELS[comparison.before.source] || '';
  const afterSource = PORTFOLIO_SOURCE_LABELS[comparison.after.source] || '';

  return (
    <Section className={className}>
      <Header>
        <div>
          <CompareArrowsOutlinedIcon />
          <span>
            <strong>{comparison.title || 'Avant / Après'}</strong>
            <small>Faites glisser le curseur pour comparer le résultat.</small>
          </span>
        </div>
        <PositionText>{position}%</PositionText>
      </Header>

      <Frame>
        <Layer>
          <FirebaseImage
            src={comparison.before.url}
            fallbackSrc="/images/project-placeholder.svg"
            alt="Photo avant les travaux"
          />
        </Layer>
        <AfterLayer style={{ clipPath: `inset(0 0 0 ${position}%)` }}>
          <FirebaseImage
            src={comparison.after.url}
            fallbackSrc="/images/project-placeholder.svg"
            alt="Photo après les travaux"
          />
        </AfterLayer>

        <BeforeLabel>Avant{beforeSource ? ` · ${beforeSource}` : ''}</BeforeLabel>
        <AfterLabel>Après{afterSource ? ` · ${afterSource}` : ''}</AfterLabel>
        <Divider style={{ left: `${position}%` }} aria-hidden="true">
          <Handle><CompareArrowsOutlinedIcon /></Handle>
        </Divider>
        <Range
          type="range"
          min="0"
          max="100"
          value={position}
          aria-label="Comparer la photo avant et la photo après"
          onChange={(event) => setPosition(Number(event.target.value))}
        />
      </Frame>
    </Section>
  );
};

const Section = styled.section`
  display: grid;
  gap: .75rem;
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid #e1e8ec;
  border-radius: 1rem;
  background: #faf8f4;
`;
const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  color: #29465a;

  > div { display: flex; align-items: center; gap: .65rem; }
  > div > svg { color: #a5741b; }
  span { display: grid; gap: .08rem; }
  small { color: #6c7b85; }
`;
const PositionText = styled.span`padding: .32rem .5rem; border-radius: 999px; background: #f1ece5; color: #526a79; font-size: .72rem; font-weight: 850;`;
const Frame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: .9rem;
  background: #efede8;
  user-select: none;

  &:focus-within {
    box-shadow: 0 0 0 4px rgba(35, 103, 148, .24);
  }
`;
const Layer = styled.div`
  position: absolute;
  inset: 0;
  background: #efede8;

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: contain;
    padding: .45rem;
    box-sizing: border-box;
  }
`;
const AfterLayer = styled(Layer)`z-index: 2;`;
const Label = styled.span`
  position: absolute;
  z-index: 5;
  top: .7rem;
  max-width: calc(50% - 1rem);
  overflow: hidden;
  padding: .38rem .55rem;
  border-radius: 999px;
  background: rgba(0,0,0,.8);
  color: #fff;
  font-size: .68rem;
  font-weight: 850;
  text-overflow: ellipsis;
  white-space: nowrap;
  backdrop-filter: blur(8px);
`;
const BeforeLabel = styled(Label)`left: .7rem;`;
const AfterLabel = styled(Label)`right: .7rem; background: rgba(151,102,18,.9);`;
const Divider = styled.div`
  position: absolute;
  z-index: 6;
  top: 0;
  bottom: 0;
  width: 3px;
  transform: translateX(-50%);
  background: #fff;
  box-shadow: 0 0 0 1px rgba(0,0,0,.18), 0 0 .8rem rgba(0,0,0,.35);
  pointer-events: none;
`;
const Handle = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 2.8rem;
  height: 2.8rem;
  display: grid;
  place-items: center;
  transform: translate(-50%,-50%);
  border: 2px solid #fff;
  border-radius: 50%;
  background: #111111;
  color: #fff;
  box-shadow: 0 .5rem 1.2rem rgba(0,0,0,.3);

  svg { font-size: 1.25rem; }
`;
const Range = styled.input`
  position: absolute;
  z-index: 7;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: ew-resize;
`;

export default BeforeAfterComparison;
